import { Component, inject, OnInit, ViewChild } from '@angular/core';
import {
  FormsModule,
  ReactiveFormsModule,
  FormBuilder,
  FormGroup,
  Validators,
  AbstractControl,
} from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { NewsService } from '../../../shared/services/news/news.service';
import { MatButtonModule } from '@angular/material/button';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { LiveAnnouncer } from '@angular/cdk/a11y';
import { MatSort, MatSortModule, Sort } from '@angular/material/sort';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatTimepickerModule } from '@angular/material/timepicker';
import {
  provideNativeDateAdapter,
  MAT_DATE_FORMATS,
  MAT_DATE_LOCALE,
} from '@angular/material/core';
import { UploadFilesComponent } from '../../../shared/components/upload-files/upload-files.component';
import { LoaderService } from '../../../shared/services/loader/loader.service';
import { LoaderComponent } from '../../../shared/components/loader/loader.component';
import { MatSnackBar } from '@angular/material/snack-bar';
import { QuillModule } from 'ngx-quill';
import { QuillConfigService } from '../../../shared/services/quill-config.service';
import { RolesService } from '../../../shared/services/roles/roles.service';
import { StorageService } from '../../../shared/services/storage/storage.service';

// Define custom date formats
export const MY_DATE_FORMATS = {
  parse: {
    dateInput: 'DD/MM/YYYY',
  },
  display: {
    dateInput: 'DD/MM/YYYY',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY',
  },
};

@Component({
  selector: 'app-edit-detail-activities',
  imports: [
    MatIconModule,
    MatFormFieldModule,
    MatSelectModule,
    ReactiveFormsModule,
    MatInputModule,
    MatButtonModule,
    MatPaginatorModule,
    MatTableModule,
    MatSortModule,
    MatCheckboxModule,
    MatDatepickerModule,
    FormsModule,
    MatTimepickerModule,
    UploadFilesComponent,
    RouterLink,
    QuillModule,
    LoaderComponent,
  ],
  providers: [
    provideNativeDateAdapter(),
    { provide: MAT_DATE_FORMATS, useValue: MY_DATE_FORMATS },
    { provide: MAT_DATE_LOCALE, useValue: 'es-ES' },
  ],
  templateUrl: './edit-detail-activities.component.html',
  styleUrls: [
    './edit-detail-activities.component.css',
    '../../../shared/styles/layout.css',
  ],
})
export class EditDetailActivitiesComponent implements OnInit {
  value: Date | undefined;
  modules: any;
  myroles: any = {};
  event: any = [];
  id: number = 0;

  // Variable para la API key del editor
  editorApiKey: string = '9htas5nurjoml4v18nn03c5tscxa2fv6kla5qdwsp5ehd7nd';

  // Para la fecha destacada
  isFeatured: boolean = false;

  // Replace individual FormControls with a FormGroup
  activityForm: FormGroup;

  toppingList: string[] = ['Iniciada', 'En trámite', 'Aceptada', 'Declinada'];

  // Define the columns to display
  displayedColumns: string[] = [
    'observation',
    'new_status',
    'updated_status_date',
  ];
  dataSource: MatTableDataSource<any> = new MatTableDataSource<any>();

  private _liveAnnouncer = inject(LiveAnnouncer);
  private _snackBar = inject(MatSnackBar);

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild(UploadFilesComponent) uploadFilesComponent!: UploadFilesComponent;

  // Para las múltiples fechas de actividad
  activityDates: Date[] = [new Date()];

  isProgrammed: boolean = false;
  tagsValue: any[] = []; // Change type to any[] to handle tag objects
  tagNamesString: string = ''; // Add this to store tag names as string
  dateValue: Date | null = null;
  timeValue: string = '00:00';

  // Add properties for image handling (add these if not already present)
  selectedFiles: File[] = [];
  selectedImage: File | null = null;
  currentImageUrl: string | null = null;
  imageRemoved: boolean = false;

  // Add property to track if validation has been attempted
  validationAttempted = false;

  // Check if at least one date is selected
  get hasMinimumDates(): boolean {
    return this.event.event_days && this.event.event_days.length > 0;
  }

  // Show date error when validation is attempted and no dates exist
  get showDateError(): boolean {
    return this.validationAttempted && !this.hasMinimumDates;
  }

  constructor(
    private route: ActivatedRoute,
    private newsService: NewsService,
    private router: Router,
    private fb: FormBuilder,
    private loaderService: LoaderService,
    private quillConfigService: QuillConfigService,
    private rolesService: RolesService,
    private storageService: StorageService
  ) {
    this.modules = this.quillConfigService.getDefaultModules();
    this.activityForm = this.fb.group({
      title: ['', [Validators.required]],
      description: ['', [Validators.required]],
      location: [''],
      registration_link: [
        '',
        [
          // Custom validator that only validates if the field is not empty
          (control: AbstractControl) => {
            if (!control.value || control.value.trim() === '') {
              return null; // No validation error for empty values
            }

            const value = control.value.trim();

            // More comprehensive URL pattern that accepts various formats
            const urlPattern =
              /^(https?:\/\/)?([\da-z\.-]+)\.([a-z\.]{2,6})([\/\w \.-]*)*\/?(\?[;&a-z\d%_\.,~#=]*)?(\#[-a-z\d_]*)?$/i;

            // Also accept URLs without protocol
            const simplePattern = /^[\w\.-]+\.[\w]{2,}(\/.*)?$/i;

            return urlPattern.test(value) || simplePattern.test(value)
              ? null
              : { pattern: true };
          },
        ],
      ],
    });
  }

  announceSortChange(sortState: Sort) {
    if (sortState.direction) {
      this._liveAnnouncer.announce(`Sorted ${sortState.direction}ending`);
    } else {
      this._liveAnnouncer.announce('Sorting cleared');
    }
  }

  ngAfterViewInit() {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
  }

  get isLoading() {
    return this.loaderService.getLoader();
  }

  // Función para mostrar un snackbar
  openSnackBar(message: string) {
    this._snackBar.open(message, 'Cerrar', {
      duration: 5000,
    });
  }

  getFormattedDateTime(): string {
    if (!this.dateValue || !this.timeValue) {
      return 'Fecha y hora no especificadas';
    }

    const date = new Date(this.dateValue);
    const [hours, minutes] = this.timeValue.split(':').map(Number);

    date.setHours(hours || 0);
    date.setMinutes(minutes || 0);

    return date.toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  // When you need the combined datetime value (e.g., for saving)
  getCombinedDateTime(): Date | null {
    if (!this.dateValue) return null;
    const base = this.dateValue;
    const parts = this.timeValue.split(':');
    const hh = parseInt(parts[0] || '0', 10);
    const mm = parseInt(parts[1] || '0', 10);
    const ss = parseInt(parts[2] || '0', 10);
    const local = new Date(
      base.getFullYear(),
      base.getMonth(),
      base.getDate(),
      isNaN(hh) ? 0 : hh,
      isNaN(mm) ? 0 : mm,
      isNaN(ss) ? 0 : ss,
      0
    );
    return local; // caller uses toISOString() for true UTC
  }

  // Add onFileSelected method to handle file selection
  onFileSelected(files: File[]): void {
    if (files && files.length > 0) {
      this.selectedImage = files[0];
      this.selectedFiles = files;
      this.imageRemoved = false; // Clear removal flag when new image is selected
    }
  }

  // Add removeImage method
  removeImage(): void {
    this.currentImageUrl = null;
    this.selectedImage = null;
    this.selectedFiles = [];
    this.imageRemoved = true;
  }

  // Handle date changes from datepicker
  onDateChange(day: any, event: any): void {
    // Convert the date to ISO string format YYYY-MM-DD
    if (event.value) {
      const date = new Date(event.value);
      // console.log(`Raw selected date from datepicker:`, date);

      // Log the date components to see what's happening
      // console.log(`Date components:`, {
      //   year: date.getFullYear(),
      //   month: date.getMonth() + 1, // 0-based, so add 1
      //   day: date.getDate(),
      //   hours: date.getHours(),
      //   minutes: date.getMinutes(),
      //   timezone: date.getTimezoneOffset(),
      // });

      // Format the date manually to preserve the selected day without timezone issues
      const year = date.getFullYear();
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const selectedDay = date.getDate().toString().padStart(2, '0');

      // Create YYYY-MM-DD format manually
      day.date = `${year}-${month}-${selectedDay}`;

      // console.log(
      //   `Date changed for day ${day.id} to ${day.date} (manually formatted)`
      // );
    }
  }

  // Handle time changes for start and end times
  onTimeChange(day: any, type: 'start' | 'end'): void {
    // Ensure time format is correct (HH:MM:SS)
    if (type === 'start') {
      // Make sure it has seconds
      if (!day.start_time.includes(':')) {
        day.start_time += ':00';
      } else if (day.start_time.split(':').length === 2) {
        day.start_time += ':00';
      }
      // console.log(`Start time changed for day ${day.id} to ${day.start_time}`);
    } else {
      // Make sure it has seconds
      if (!day.end_time.includes(':')) {
        day.end_time += ':00';
      } else if (day.end_time.split(':').length === 2) {
        day.end_time += ':00';
      }
      // console.log(`End time changed for day ${day.id} to ${day.end_time}`);
    }
  }

  // Método para eliminar una fecha
  removeDate(index: number): void {
    if (this.activityDates.length > 1) {
      this.activityDates.splice(index, 1);
    }
  }

  // Add a method to update tags when input changes
  updateTags(tagString: string) {
    // Split the input by commas and create tag-like objects
    const tagNames = tagString
      .split(',')
      .map((name) => name.trim())
      .filter((name) => name);
    // Keep the original IDs if available, or set to null for new tags
    this.tagsValue = tagNames.map((name) => {
      const existingTag = this.tagsValue.find((t) => t.name === name);
      return existingTag || { name, id: null };
    });
  }

  // Update your addNewDate method to add to event_days instead
  addNewDate() {
    if (!this.event.event_days) {
      this.event.event_days = [];
    }

    // Create a new event day with default values
    const today = new Date();
    const year = today.getFullYear();
    const month = (today.getMonth() + 1).toString().padStart(2, '0');
    const day = today.getDate().toString().padStart(2, '0');

    const newDay: any = {
      id: this.getNextEventDayId(),
      date: `${year}-${month}-${day}`, // Today's date in YYYY-MM-DD format
      start_time: '09:00:00',
      end_time: '17:00:00',
    };

    this.event.event_days.push(newDay);

    // Reset validation state when user adds a date
    this.validationAttempted = false;
  }

  // Remove an event day by ID without checking length
  removeEventDay(id: number) {
    if (this.event.event_days) {
      const index = this.event.event_days.findIndex(
        (day: any) => day.id === id
      );
      if (index !== -1) {
        this.event.event_days.splice(index, 1);

        // Reset validation state when user removes a date
        this.validationAttempted = false;
      }
    }
  }

  // Helper method to generate a unique ID for new event days
  private getNextEventDayId(): number {
    if (!this.event.event_days || this.event.event_days.length === 0) {
      return 1;
    }

    // Find the maximum ID and add 1
    const maxId = Math.max(...this.event.event_days.map((day: any) => day.id));
    return maxId + 1;
  }

  getScheduledPublicationDate(): Date | null {
    if (this.event && this.event.publication_date) {
      return new Date(this.event.publication_date);
    }
    return null;
  }

  getScheduledPublicationTime(): string {
    if (this.event && this.event.publication_date) {
      const date = new Date(this.event.publication_date);
      const hours = date.getHours().toString().padStart(2, '0');
      const minutes = date.getMinutes().toString().padStart(2, '0');
      return `${hours}:${minutes}`;
    }
    return '';
  }

  loadRoles() {
    // Get permissions using the service method
    const permissions = this.rolesService.getPermissionsForFunctionality(
      'activities',
      this.storageService
    );

    if (permissions) {
      // Store permissions in myroles
      this.myroles = permissions;

      // Check write permission and redirect if needed
      this.rolesService.checkWritePermission(
        'activities',
        this.storageService,
        this._snackBar,
        this.router,
        '/home/communication/activities',
        'No tienes permisos para editar una actividad'
      );
    } else {
      // console.log('No user roles found in storage');
    }
  }

  ngOnInit() {
    this.loaderService.showLoader();
    this.route.params.subscribe((params) => {
      this.id = params['id'];
      this.newsService.getActivitiesId(this.id).subscribe({
        next: (response) => {
          this.event = response;

          // Initialize image properties correctly
          this.currentImageUrl = response.image_url || null;
          this.selectedImage = null;
          this.imageRemoved = false;

          // Use patchValue for form initialization
          this.activityForm.patchValue({
            title: response.title,
            description: response.description,
            // location: response.location || '',
            registration_link: response.registration_link || '',
          });

          // Log the registration link for debugging
          // console.log(
          //   'Registration link from API:',
          //   response.registration_link
          // );
          // console.log(
          //   'Form registration_link value after patch:',
          //   this.activityForm.get('registration_link')?.value
          // );

          this.tagsValue = response.tags || [];

          // Store the current image URL
          this.currentImageUrl = response.image_url || response.image || null;

          this.tagNamesString = this.tagsValue
            .map((tag: any) => tag.name)
            .join(', ');

          // Initialize date and time values from publication_date when component loads
          if (this.event && this.event.publication_date) {
            const pubDate = new Date(this.event.publication_date);
            this.dateValue = pubDate;

            // Format time as HH:MM for the time input
            const hours = pubDate.getHours().toString().padStart(2, '0');
            const minutes = pubDate.getMinutes().toString().padStart(2, '0');
            this.timeValue = `${hours}:${minutes}`;
          }
          this.loaderService.hideLoader();
        },
        error: (error) => {
          this.loaderService.hideLoader();
          this.openSnackBar(
            'Error al cargar la actividad. Por favor, inténtalo de nuevo.'
          );
          console.error(error);
        },
      });
    });

    this.loadRoles();
  }

  saveChanges() {
    // Set validation attempted flag
    this.validationAttempted = true;

    // Check if at least one date is provided
    if (!this.event.event_days || this.event.event_days.length === 0) {
      this.openSnackBar(
        'Debe seleccionar al menos una fecha para la actividad'
      );
      return;
    }

    if (this.activityForm.invalid) {
      this.activityForm.markAllAsTouched();
      // Check specifically for URL format issues
      if (this.activityForm.get('registration_link')?.hasError('pattern')) {
        this.openSnackBar('Error, formato incorrecto en el enlace');
      } else {
        this.openSnackBar('Por favor complete todos los campos requeridos');
      }
      return;
    }

    this.loaderService.showLoader();
    const formValues = this.activityForm.value;

    // Log form values before submission for debugging
    // console.log('Form values before submission:', formValues);

    // Create combined datetime for publication if scheduled
    let publication_date = this.event.publication_date;
    if (this.event.scheduled_publication && this.dateValue) {
      // Create a new date from the selected date
      const combinedDate = new Date(this.dateValue);

      // Parse time values
      const [hours, minutes] = this.timeValue.split(':').map(Number);

      // Set hours and minutes
      combinedDate.setHours(hours || 0);
      combinedDate.setMinutes(minutes || 0);
      combinedDate.setSeconds(0);

      // Convert to ISO string for the API
      publication_date = combinedDate.toISOString();
    }

    // Filter out any event days with null dates and format the remaining ones
    const formattedEventDays = this.event.event_days
      .filter((day: any) => day && day.date) // Filter out null days or days with null dates
      .map((day: any) => {
        // Ensure date is in YYYY-MM-DD format if it's a Date object
        let formattedDate = day.date;
        if (day.date instanceof Date) {
          // Format the date manually to preserve the selected day without timezone issues
          const year = day.date.getFullYear();
          const month = (day.date.getMonth() + 1).toString().padStart(2, '0');
          const selectedDay = day.date.getDate().toString().padStart(2, '0');

          // Create YYYY-MM-DD format manually
          formattedDate = `${year}-${month}-${selectedDay}`;
        }

        // Ensure times have the correct format (HH:MM:SS)
        let startTime = day.start_time || '00:00:00';
        if (startTime.split(':').length === 2) {
          startTime += ':00';
        }

        let endTime = day.end_time || '00:00:00';
        if (endTime.split(':').length === 2) {
          endTime += ':00';
        }

        // console.log(
        //   `Formatting event day: date=${formattedDate}, start=${startTime}, end=${endTime}`
        // );

        return {
          date: formattedDate,
          start_time: startTime,
          end_time: endTime,
        };
      });

    // Make sure we have at least one event day
    if (formattedEventDays.length === 0) {
      // Add a default event day if none exist
      const today = new Date();
      const year = today.getFullYear();
      const month = (today.getMonth() + 1).toString().padStart(2, '0');
      const day = today.getDate().toString().padStart(2, '0');

      formattedEventDays.push({
        date: `${year}-${month}-${day}`,
        start_time: '09:00:00',
        end_time: '17:00:00',
      });
    }

    // Convert tags array to tags_string
    const tagsString = this.tagsValue.map((tag: any) => tag.name).join(', ');

    // Create FormData to properly handle image uploads
    const formData = new FormData();

    // Use values from the first date [0], even if there are multiple days
    const firstDate = formattedEventDays[0];
    formData.append('event_date', firstDate.date);
    formData.append('start_time', firstDate.start_time);
    formData.append('end_time', firstDate.end_time);

    // Add basic text fields
    // Preserve emojis (including trailing ones) by preferring the Quill editor DOM HTML
    let description = formValues.description;
    try {
      const editor = document.querySelector('.ql-editor');
      const html = editor?.innerHTML;
      if (html && html.trim() !== '') {
        description = html;
      }
    } catch (error) {
      // ignore - fallback to form value
    }
    formData.append('description', description || '');
    formData.append('location', formValues.location || '');
    formData.append('featured', String(this.event.featured));
    formData.append('published', String(this.event.published || true));
    formData.append('title', formValues.title || this.event.title);
    formData.append(
      'scheduled_publication',
      formValues.isProgrammed ? 'true' : 'false'
    );

    // Only append registration_link if it's not empty and is a valid URL
    if (
      formValues.registration_link &&
      formValues.registration_link.trim() !== ''
    ) {
      let url = formValues.registration_link.trim();
      // Ensure URL has a protocol
      if (!url.startsWith('http://') && !url.startsWith('https://')) {
        url = 'https://' + url;
      }
      formData.append('registration_link', url);
    }

    // Remove this duplicate assignment which was overriding the value
    // if (this.event.registration_link) {
    //   formData.append('registration_link', this.event.registration_link);
    // }

    formData.append('notifications', String(this.event.notifications));

    // Add publication date if available
    if (publication_date) {
      formData.append('publication_date', publication_date);
    }

    // Add tags
    formData.append('tags_string', tagsString);

    // Don't append event_days as a JSON string
    // formData.append('event_days', JSON.stringify(formattedEventDays));

    // Instead, append each day's properties individually
    formattedEventDays.forEach((day: any, index: number) => {
      formData.append(`event_days[${index}][date]`, day.date);
      formData.append(`event_days[${index}][start_time]`, day.start_time);
      formData.append(`event_days[${index}][end_time]`, day.end_time || '');
    });

    // Debug image state
    // console.log('Image state before sending request:', {
    //   selectedFiles:
    //     this.selectedFiles.length > 0
    //       ? this.selectedFiles.map((f) => f.name)
    //       : 'No new files',
    //   imageRemoved: this.imageRemoved,
    //   currentImageUrl: this.currentImageUrl,
    // });

    // Handle image upload and removal properly:
    // 1. If new image is selected, upload it (this will automatically replace the old one)
    // 2. If no new image but imageRemoved is true, set the remove_image flag
    // 3. If neither, keep the existing image

    if (this.selectedImage) {
      // New image selected - add it to FormData (will replace existing)
      formData.append('image', this.selectedImage);
    } else if (this.imageRemoved) {
      // No new image, but user wants to remove existing image
      formData.append('remove_image', 'true');
    }
    // If neither condition is met, we keep the existing image

    // Log FormData entries for debugging
    // console.log('FormData entries:');
    // formData.forEach((value, key) => {
    //   console.log(`${key}: ${value instanceof File ? value.name : value}`);
    // });

    // console.log('Submitting FormData:', formData);

    // Call the service to update the event with FormData
    this.newsService.putActivities(this.id, formData).subscribe({
      next: (response) => {
        // console.log('Event updated successfully:', response);
        this.loaderService.hideLoader();
        this.openSnackBar('Actividad actualizada con éxito.');
        this.router.navigate(['/home/communication/activities']);
      },
      error: (error) => {
        console.error('Error updating event:', error);
        this.loaderService.hideLoader();
        this.openSnackBar(
          'Error al actualizar la actividad. Inténtalo de nuevo.'
        );
      },
    });
  }
}
